/**
 * journeyModel.ts — the Patient journey panel's whole derivation, with no React
 * in it: which lane a patient came down, which phase order that lane implies,
 * what happened at each rung, and what the action band should say.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY A SEPARATE PURE MODULE. The panel is a rail of ~14 rungs whose ORDER
 * changes with the lane and whose states are read off six different server
 * facts. Every one of those judgements is a sentence somebody will act on — "no
 * card was ever collected" sends a patient a link, "authorised" tells a
 * clinician their approval takes the money — so they have to be testable
 * without rendering anything, and they have to live in ONE place rather than
 * being re-derived by the band and then again by the rail.
 *
 * ── R41: THE SERVER IS THE CONTROL ───────────────────────────────────────────
 * Nothing here decides anything. Every rung is a READ of a fact the owning
 * service already stated — `submittedAt`, `consentAgreedAt`, the payment
 * posture's rung, the order's state, the pharmacy's `dispatchedAt`. The panel
 * writes the SENTENCE; the services own the FACT. Where a fact does not exist,
 * this module renders an em-dash and says so — it never infers one.
 *
 * ── ⚠️ THE ONE RULE THAT MUST NOT BEND ───────────────────────────────────────
 * `card_on_file` IS NOT AN AUTHORISATION. `OrdersService.approve()` requires a
 * `month1_preauth` in `held`/`captured` and throws on every lower rung, and on
 * production today EVERY `month1_preauth` is `refunded` and NONE is `held`. So
 * "Card saved" and "Payment authorised" are TWO rungs here, not the single
 * "Card saved & authorised" step the design brief sketched. Collapsing them
 * would put a green tick beside a patient whose approval 409s — see
 * `paymentPosture.ts`, whose `CHARGEABLE_POSTURES` this module asks rather than
 * re-deriving.
 *
 * ── ⚠️ MONEY IS ROLE-SHAPED ──────────────────────────────────────────────────
 * The pence fields exist only on an ADMIN posture and this module still gates on
 * the ROLE as well as on presence, so a mis-shaped row can never put a figure on
 * a clinician's screen. `clinicians/clinicians.spec.tsx` sweeps the whole
 * clinician drawer for `£ / pence / price`.
 *
 * ── R19 ──────────────────────────────────────────────────────────────────────
 * Ids, enums, dates and counts. No answer values, no address, no contact
 * details. The one free-text string that reaches a rung is the pharmacy's coarse
 * `dispenseRejectedReason`, which is an ops enum-ish token, not clinical prose.
 */
import type {
  DealCard,
  DealDetail,
  DealEntryPath,
  DealPaymentPosture,
  StaffOrder,
  UserRole,
} from '../api/types';
import { READINESS_STAFF_LABELS, type LeadReadinessKey } from '../journey/leadReadiness';
import { formatPence } from '../lib/format';
import { displayTreatmentName } from '../lib/patientStatusCopy';
import type { QuestionnaireProgress } from './questionnaireProgress';
import {
  isChargeablePosture,
  isTerminalOrderState,
  orderHasPharmacyWork,
  orderReachedPharmacy,
  postureIsKnown,
  postureReached,
} from './paymentPosture';
import { clinicalStatusFor } from './statusChips';
import { TERMINAL_STAGES } from './stages';

/* ══════════════════════════════════════════════════════════════════════════
 * THE SHAPES
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * FOUR STEP STATES, AND THE PANEL ENCODES EACH IN SHAPE AS WELL AS COLOUR
 * (CTO 2026-09-07: "colour alone fails a colour-blind reader and a screenshot").
 * The names are the states, not the shapes, so the rendering can change without
 * this module learning about circles.
 */
export type JourneyStepState =
  /** It happened. Filled tick. */
  | 'done'
  /** The outstanding step on a live deal. Ringed dot. */
  | 'now'
  /** The journey ENDED here. Coral ring with a bar. */
  | 'stopped'
  /** Never got this far. Dashed outline. */
  | 'not_reached';

/** Whose move it is — the small label on the action band. */
export type JourneyOwner = 'PATIENT' | 'CLINICIAN' | 'ADMIN';

/** One rung of the rail. */
export interface JourneyStep {
  id: string;
  label: string;
  state: JourneyStepState;
  /**
   * ⚠️ THE RIGHT-HAND COLUMN IS ONLY EVER A DATE OR AN EM-DASH — never a count
   * (CTO). That is why this is a bare ISO instant and every count, tally and
   * qualifier lives in `note` instead, UNDER the step name.
   *
   * NULL ON A `done` RUNG IS REAL AND COMMON, not a bug: most of the journey's
   * beats are recorded as STATE rather than as an instant. A saved card, a
   * chosen plan, a delivery address and an issued prescription are each a fact
   * with no timestamp anywhere on the wire this panel reads. An em-dash beside a
   * tick says "this happened; nobody recorded when" — which is the truth, and a
   * borrowed neighbouring date would be a confident wrong one on a clinical
   * record. See the per-rung notes for which seam field would fill each.
   */
  at: string | null;
  /**
   * WHO did this step when it is not a person on our side — the small tag beside
   * the name, the rung-level half of the phase caption.
   *  - `'auto'`: it runs with no human, so the automatic chain reads as a chain.
   *    Capture, prescription issue and the pharmacy handoff all fire from
   *    `fulfilment.service.ts` off a single approval.
   *  - `'pharmacy'`: a pharmacist records it in the dispensing console. That is
   *    dispatch (CTO, 2026-09-11: it was wrongly tagged auto — it is not).
   * Absent on every rung a person here does.
   */
  by?: 'auto' | 'pharmacy';
  /** The line UNDER the step name. Counts belong here, never in the date column. */
  note?: string;
  /**
   * 0–100 for the questionnaire's thin bar. Absent on every other rung — and
   * absent on that one too wherever the bar would contradict the marker beside
   * it, because a part-full bar under a green tick is a completeness claim and
   * its denial in the same row. See the WHY in `formRungs`.
   */
  percent?: number;
}

/**
 * THE GROUP IDS, AS A CLOSED SET.
 *
 * WHY A UNION AND NOT `string` (2026-09-12). The reading order of the groups is
 * now a lane-keyed TABLE (`NARRATIVE_ORDER`) rather than the order they happen
 * to be pushed in, and a table keyed on a free-form string is a table that can
 * silently miss an entry: an unranked group would sort to one end of the rail
 * with nothing failing. With the ids closed, the compiler checks both rows of
 * that table for exhaustiveness, and `narrativeRank` can be total by
 * construction rather than by a defensive fallback.
 */
export type JourneyPhaseId = 'arrival' | 'qualification' | 'call' | 'form' | 'decision' | 'hold' | 'supply';

/** A group of rungs under one small header rule. */
export interface JourneyPhase {
  id: JourneyPhaseId;
  title: string;
  steps: JourneyStep[];
  /**
   * WHOSE STEP THIS STAGE IS IN THE MODEL — "Patient", "Clinician", "Us and the
   * pharmacy". Rendered as a muted caption on the header, never as a pill.
   *
   * ⚠️ IT IS NOT A CLAIM ABOUT WHO OWES THE NEXT MOVE ON THIS CARD. The action
   * band's own owner pill is the single answer to "is this mine", and two pills
   * in two grammars on one panel would be read as one. This says "in this
   * platform, the plan step is the patient's" — a fact about the model, true of
   * every card, and therefore safe to render on a group that nobody is waiting
   * on.
   */
  owner?: string;
  /**
   * One sentence under the header, for something true of the GROUP rather than
   * of any rung in it. Two uses today: the S-2 capture gap on `arrival`, and
   * the card-vs-decision ORDER on `decision`.
   *
   * ⚠️ THE ORDER LIVES HERE AND NOT IN THE LAYOUT, which is the whole point of
   * the 2026-09-11 regrouping — see `sequenceNote`.
   */
  note?: string;
}

/**
 * The action band: the outstanding step, whose move it is, and the controls that
 * perform it.
 *
 * ⚠️ THREE TONES, NOT THE TWO THE BRIEF NAMED. Amber "blocked on someone" and
 * green "the next step is automatic" both assert that something is still due; a
 * CLOSED deal has nothing due, and painting it either colour would be a claim.
 * `neutral` is that third case and only that case.
 */
export interface JourneyBand {
  tone: 'amber' | 'green' | 'neutral';
  /** Null when nothing is outstanding — a closed or fully-delivered journey. */
  owner: JourneyOwner | null;
  /** The outstanding step, as a sentence. */
  headline: string;
  /** What we hold, or do not. Null when there is nothing to add. */
  detail: string | null;
  /** Which reused controls this band carries — see `JourneyPanel` for the mounts. */
  actions: {
    /** `pipeline/PlanLinkActions` — the patient's own journey link. CLINICIAN + ADMIN. */
    planLink: boolean;
    /** `admin/PaymentLinkAction` — a LIVE Stripe page. ADMIN, and see the gate below. */
    paymentLink: boolean;
  };
}

/** Everything the panel renders. */
export interface Journey {
  lane: JourneyLane;
  /** The ROUTE, not just the source — "Web form → self-serve". */
  entryLabel: string;
  phases: JourneyPhase[];
  band: JourneyBand;
}

/**
 * The lanes, by how the patient reached a clinician.
 *
 * `self_serve` — they filled the form themselves (`widget_form` /
 * `portal_journey`). `clinician_led` — a human was in the loop from the start
 * (`callback_button`, and the staff-created `staff_sql` deal, which is the same
 * shape: somebody sat with the patient).
 */
export type JourneyLane = 'self_serve' | 'clinician_led';

/** What the panel takes. Every field is something the drawer already holds. */
export interface JourneyInput {
  deal: DealCard;
  assessment: DealDetail['assessment'];
  callback: DealDetail['callback'];
  /** The drawer's merged feed — the only source of a call-attempt instant. */
  activity: DealDetail['activity'];
  /**
   * The drawer aggregate's closure block. The ONE decision instant this panel
   * can actually read: a rejection is recorded with a `closedAt`, an approval is
   * not. Absent on a clinic-service that predates stage clarity.
   */
  outcome: DealDetail['outcome'];
  /** The `StaffOrder` behind `orderCaseRef`, when the drawer has resolved one. */
  order: StaffOrder | null;
  /** The viewer's role. Gates the money half of the band and nothing else. */
  role: UserRole;
  /** Null until the two questionnaire reads land, or if they failed. */
  questionnaire: QuestionnaireProgress | null;
}

/* ══════════════════════════════════════════════════════════════════════════
 * LANE + ROUTE
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ THE CHIP NAMES THE ROUTE, NOT THE SOURCE (CTO 2026-09-07). "Widget form"
 * says where a record came from; "Web form → self-serve" says what happens next,
 * which is the thing a desk opening the card needs. The existing `ENTRY_LABEL`
 * in `stages.ts` stays exactly as it is — it is the board card's provenance tag
 * and four other surfaces read it.
 */
const ROUTE_LABEL: Record<DealEntryPath, string> = {
  widget_form: 'Web form → self-serve',
  portal_journey: 'Web form → self-serve',
  callback_button: 'Call back → clinician-led',
  // The staff-created SQL deal: a clinician started the record and, usually,
  // filled the form with the patient on the phone. Clinician-led in every way
  // that matters to this panel; named for what it is so nobody reads it as a
  // patient who found us.
  staff_sql: 'Created by staff → clinician-led',
};

/** Which lane an entry path belongs to. */
export function journeyLane(entryPath: DealEntryPath): JourneyLane {
  return entryPath === 'widget_form' || entryPath === 'portal_journey' ? 'self_serve' : 'clinician_led';
}

/* ══════════════════════════════════════════════════════════════════════════
 * INTERNAL RUNG SHAPE
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * A rung before the four states are assigned. `reached` is the fact; `halt`
 * marks the rung the journey ENDED on, which overrides everything.
 */
interface Rung {
  id: string;
  label: string;
  reached: boolean;
  at: string | null;
  by?: 'auto' | 'pharmacy';
  note?: string;
  percent?: number;
  halt?: boolean;
  /**
   * The PHARMACY owes this step right now. On a closed deal the first unreached
   * rung normally reads "Stopped here" — but every paid order sits on a Won
   * card, and while the pharmacy still holds it the journey has not stopped: it
   * is their move. Set on the dispatched rung only, from the same predicate the
   * pharmacy sign-in button uses, so the two can never disagree.
   */
  awaitingPharmacy?: boolean;
  /**
   * A CLINICIAN owes this step right now — the exact mirror of
   * `awaitingPharmacy`. An approved, paid, not-yet-prescribed order sits on a
   * Won card, so without this the first unreached rung would read "Stopped
   * here" in coral: a dead journey, on a live patient who is waiting for a
   * prescription. Set on `prescription_created` only.
   */
  awaitingClinician?: boolean;
}

/**
 * Assign the four states across an ORDERED list of rungs.
 *
 * ONE PASS, THREE RULES, in precedence order:
 *  1. An explicit `halt` rung is `stopped` — the clinician declined, the engine
 *     hard-stopped, the pharmacy refused. Everything after it is `not_reached`
 *     whatever it says, because nothing after a stop can have happened.
 *  2. Otherwise every `reached` rung is `done`.
 *  3. The FIRST unreached rung is `now` on a live deal and `stopped` on a closed
 *     one. That single line is why a closed deal does not need a second
 *     derivation: "where did it end" and "what is outstanding" are the same
 *     question asked of a dead and a live record.
 */
function assignStates(rungs: Rung[], closed: boolean): JourneyStep[] {
  const haltIndex = rungs.findIndex((r) => r.halt === true);
  const firstUnreached = rungs.findIndex((r) => !r.reached);

  return rungs.map((rung, index) => {
    let state: JourneyStepState;
    if (haltIndex === index) {
      state = 'stopped';
    } else if (rung.reached) {
      /**
       * ⚠️ A FACT OUTRANKS THE HALT, AND THIS ORDERING CHANGED ON 2026-09-11.
       *
       * This test used to sit BELOW the post-halt rule, so everything after a
       * decline was `not_reached` "whatever the later facts say". That was
       * right while the rail was ordered by SEQUENCE: a rung after the decision
       * was genuinely later in time, so a decline meant it never happened, and
       * a `reached` flag there was contradictory data worth overriding.
       *
       * The rail is now ordered by FIXED GROUPS (see the group assembly), so
       * position is no longer time. The card group sits after the decision and
       * routinely holds facts that happened BEFORE it — a patient whose card
       * was authorised and who was then declined is the ordinary shape of a
       * decline on the paid lane. Keeping the old precedence would print
       * "Payment authorised — not reached" on a card whose money was genuinely
       * held: a false negative about MONEY on a clinical record.
       *
       * "Payment taken" still reads `not_reached` on that card, because its own
       * fact says so — `approve()` is what captures, and a declined case never
       * got there. Which is the point: the rung's own evidence decides.
       */
      state = 'done';
    } else if (haltIndex !== -1 && index > haltIndex) {
      state = 'not_reached';
    } else if (index === firstUnreached && haltIndex === -1) {
      state = closed && !rung.awaitingPharmacy && !rung.awaitingClinician ? 'stopped' : 'now';
    } else {
      state = 'not_reached';
    }
    const step: JourneyStep = { id: rung.id, label: rung.label, state, at: rung.at };
    if (rung.by) step.by = rung.by;
    if (rung.note) step.note = rung.note;
    if (rung.percent !== undefined) step.percent = rung.percent;
    return step;
  });
}

/** A phase before its rungs have been scored — see `toPhases`. */
interface PhasePlan {
  id: JourneyPhaseId;
  title: string;
  rungs: Rung[];
  owner?: string;
  note?: string;
}

/**
 * ⚠️ THE FOUR STATES ARE ASSIGNED ACROSS THE WHOLE RAIL, ONCE, AND ONLY THEN
 * SLICED INTO PHASES. This is not a tidiness preference — it is the difference
 * between a correct panel and a broken one.
 *
 * `assignStates` marks the FIRST unreached rung `now` (or `stopped`). Run it
 * per-phase and every phase gets its own "happening now" dot: a patient who has
 * not finished the questionnaire would simultaneously be "now" at the clinical
 * decision and "now" at the plan choice, and a halt in one phase would not stop
 * the next one. There is exactly ONE outstanding step in a journey, so there is
 * exactly one scoring pass over the flattened rail.
 */
function toPhases(plan: PhasePlan[], closed: boolean, lane: JourneyLane): JourneyPhase[] {
  const scored = assignStates(plan.flatMap((phase) => phase.rungs), closed);
  let cursor = 0;
  const phases = plan.map((phase) => {
    const steps = scored.slice(cursor, cursor + phase.rungs.length);
    cursor += phase.rungs.length;
    return { id: phase.id, title: phase.title, steps, owner: phase.owner, note: phase.note };
  });
  return inNarrativeOrder(phases, lane);
}

/**
 * ══════════════════════════════════════════════════════════════════════════
 * READING ORDER ≠ SCORING ORDER (2026-09-12, CTO: "clinician approval should
 * come after patient account creation").
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The CTO read the rail as a story and found it telling the wrong one: on the
 * self-serve lane the patient chooses a plan, saves a card and AUTHORISES it,
 * and only an authorised card puts the deal in front of a clinician at all
 * (`stages.ts` → `isAwaitingClinicalDecision`, the 2026-09-07 authorisation
 * gate; an unauthorised finished questionnaire parks in `warm_lead`). Printing
 * *Clinical decision* above *Plan, delivery and card* described the opposite
 * platform.
 *
 * ⚠️ SO THE GROUPS MOVED AND THE SCORING DID NOT, AND THAT SPLIT IS THE WHOLE
 * DESIGN. `assignStates` marks the FIRST unreached rung `now`, so the order it
 * sees IS the answer to "what is this patient waiting for". The order a reader
 * sees is the answer to "what happens on this route". Those are the same two
 * questions the 2026-09-11 rewrite already separated (Q1/Q2 above `sequenceNote`)
 * — this change just stops them sharing one list.
 *
 * Had the reorder moved the scoring too, the single `now` marker would have
 * jumped off the decision rung and onto *Plan chosen* for every submitted,
 * unapproved, unpaid card — the `warm_lead` population, the largest cohort on
 * the board. That would contradict TWO other surfaces that have already argued
 * this out in code: the action band (`buildBand` branch 5 → owner CLINICIAN,
 * "a clinician reviews the answers") and `REVIEW_OUTSTANDING_STAGES` in
 * `statusChips.tsx`, which puts `warm_lead` on the review-outstanding list on
 * purpose. A rail and a band disagreeing about whose move it is, on one screen,
 * is worse than either being wrong alone.
 *
 * ⚠️ AND THE READING ORDER IS STILL NOT A PER-CARD SEQUENCE CLAIM. It is keyed
 * on the LANE — a fact about the route, true of every card on it, exactly like
 * the `owner` captions — never on this card's own history. What THIS patient
 * did first remains one sentence, `sequenceNote`, on the Clinical decision
 * group, and that sentence is free to contradict the layout (and does: a
 * self-serve card can read "This decision came before any card was authorised"
 * under a rail that shows the card group first). The sentence wins, because it
 * is the only one of the two that can say "not recorded". That is why the
 * 2026-09-11 `isPaidFirst` deletion is not being undone here: that mechanism
 * swapped groups per CARD off current state, which is the thing a layout cannot
 * do honestly.
 *
 * WHY THE TABLE IS LANE-KEYED AT ALL. One order cannot be truthful for both:
 *  · `self_serve` — card, then clinician. The gate above.
 *  · `clinician_led` — clinician, then card. A human recommends on the call
 *    BEFORE any money exists (the rung's own label becomes "Clinician
 *    recommended", see `decisionRung`), the handover then emails the plan link,
 *    and the patient pays afterwards. Showing the card group first on that lane
 *    would put an empty money group above the step the clinician actually owes.
 *
 * MEMBERSHIP IS STILL FIXED: no rung migrates between groups, and no group is
 * renamed by the reorder. Only the sequence the reader walks changes.
 */
const NARRATIVE_ORDER: Record<JourneyLane, readonly JourneyPhaseId[]> = {
  // form and consents → plan, delivery and card → clinical decision → charge…
  self_serve: ['arrival', 'qualification', 'call', 'form', 'hold', 'decision', 'supply'],
  // …and the recommendation genuinely precedes the card on the other lane.
  clinician_led: ['arrival', 'qualification', 'call', 'form', 'decision', 'hold', 'supply'],
};

/**
 * Where a group sits for a reader on this lane. Total by construction — every
 * `JourneyPhaseId` is in both rows of the table and the compiler checks it — so
 * there is no "unknown group" case to fall back on and no silent -1.
 */
function narrativeRank(id: JourneyPhaseId, lane: JourneyLane): number {
  return NARRATIVE_ORDER[lane].indexOf(id);
}

/**
 * Re-sequence SCORED groups for reading. Deliberately a stable sort over the
 * finished phases rather than a re-ordering of the plan: by this point every
 * rung already carries the state it was scored with, so moving a group can no
 * longer move the `now` marker. That is the invariant, expressed as an ordering
 * of operations rather than as a comment asking the next author to be careful.
 */
function inNarrativeOrder(phases: JourneyPhase[], lane: JourneyLane): JourneyPhase[] {
  return [...phases].sort((a, b) => narrativeRank(a.id, lane) - narrativeRank(b.id, lane));
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE RUNGS
 * ════════════════════════════════════════════════════════════════════════ */

/** The newest logged call attempt — the only call instant anywhere on this aggregate. */
function lastCallAttemptAt(activity: DealDetail['activity']): string | null {
  const attempts = activity.filter((entry) => entry.kind === 'call_attempt');
  if (attempts.length === 0) return null;
  // The feed's order is not guaranteed across the two ledgers it merges, so take
  // the max rather than trusting a position.
  return attempts.reduce((newest, entry) => (entry.at > newest ? entry.at : newest), attempts[0].at);
}

/**
 * The plan in words — the humanised product key and the term, e.g.
 * "Treatment A 2 5mg · 3 months". Money-free, so BOTH roles get it.
 *
 * ⚠️ R28: this file never NAMES a medicine. The words come from the
 * `productKey` orders-service sends at read time; a literal POM name in this
 * repo's source would fail the R28 source sweep (`journey/r28.spec.tsx`), which
 * scans every file under src/ precisely so a brand name cannot reach a public
 * bundle path.
 */
function planWords(posture: DealPaymentPosture | null | undefined): string | null {
  if (!postureIsKnown(posture)) return null;
  const product = posture.productKey ? displayTreatmentName(posture.productKey, posture.productKey) : null;
  const term = posture.months ? `${posture.months} month${posture.months === 1 ? '' : 's'}` : null;
  if (!product && !term) return null;
  return [product, term].filter(Boolean).join(' · ');
}

/**
 * THE PROGRESS SENTENCE, WRITTEN ONCE AND RENDERED TWICE.
 *
 * ⚠️ THE RUNG'S NOTE AND THE ACTION BAND'S DETAIL ARE THE SAME CLAIM ABOUT THE
 * SAME FORM, and they were derived separately — which is how the panel came to
 * print "All 14 questions answered" on the rung while the band four lines above
 * it said "2 of 14 answered" under "the patient must finish the questionnaire".
 * Two derivations of one fact will eventually disagree; there is now one, and
 * the band appends nothing but a full stop.
 *
 * THREE VERDICTS, TWO VOICES. `completeness` decides WHAT is true; `submitted`
 * decides how to say it:
 *
 *  - "STOPPED AT" ONLY MEANS ANYTHING ON AN UNSUBMITTED FORM. A submitted
 *    assessment cleared `computeSubmission`, so by definition the patient did
 *    not stop — and where the two reads disagree (a bank that changed under a
 *    finished journey) the SUBMISSION is the fact and the count is the
 *    derivation. Printing "stopped at Q3" over a form we accepted would put a
 *    false claim about a patient's journey on a clinical record.
 *  - AN UNKNOWABLE COUNT SAYS SO, IN WORDS. "2 of 14 answered" is true and it is
 *    also the whole of what we know; the clause that follows it is the half a
 *    reader needs in order not to read a partial view as a partial form.
 *  - A COMPLETE COUNT WITH NO SUBMISSION IS NAMED AS SUCH, because it is the one
 *    case where every answer is in and there is still something to chase.
 */
function questionnaireLine(q: QuestionnaireProgress, submitted: boolean): string {
  const tally = `${q.answered} of ${q.total}`;
  if (q.completeness === 'incomplete') {
    return submitted ? `${tally} answers recorded` : `${tally} — stopped at Q${q.stoppedAt.number}, ${q.stoppedAt.prompt}`;
  }
  if (q.completeness === 'unknowable') {
    return submitted
      ? `${tally} answers recorded — some questions are not visible to you`
      : `${tally} answered — some questions are not visible to you`;
  }
  return submitted ? `All ${q.total} questions answered` : `All ${q.total} questions answered — not submitted yet`;
}

/**
 * THE PATIENT'S FORM. Shared by both lanes — only the phase it sits in moves.
 */
function formRungs(input: JourneyInput): Rung[] {
  const { deal, assessment, questionnaire } = input;
  const rungs: Rung[] = [];

  /**
   * THE QUESTIONNAIRE. `assessment.createdAt` is when the draft was minted,
   * which is when the patient started answering.
   *
   * The progress line is the CTO's, verbatim in shape: "All 14 questions
   * answered", or "7 of 14 — stopped at Q8, other medications" — plus the third
   * sentence the redaction forces, "2 of 14 answered — some questions are not
   * visible to you". It renders only once `useQuestionnaireProgress` has both
   * reads; until then (and if either fails) the rung stands with no line and no
   * bar, because a count nobody can verify is worse on a clinical record than no
   * count.
   */
  const started = assessment != null;
  const submitted = assessment?.submittedAt != null;
  const note = questionnaire ? questionnaireLine(questionnaire, submitted) : undefined;
  /**
   * ⚠️ THE BAR IS DRAWN ONLY WHERE IT AGREES WITH THE MARKER, and that is the
   * point of this line rather than a styling preference.
   *
   * A thin bar is the same completeness claim as the tick, in a second shape. A
   * part-full bar under a green tick says both "finished" and "not finished" in
   * one row — which is what shipped: "All 14 questions answered" over a 14% bar.
   * So a rung that ticks on the SUBMISSION alone (see below) draws a bar only
   * where the count can corroborate it, and otherwise draws none. On an
   * unfinished form the bar is safe by construction, because the marker is not a
   * tick and the bar is the progress it is measuring.
   *
   * `total` is at least 1 wherever a progress exists at all — the derivation
   * returns null for a bank holding no clinical questions — so there is no zero
   * here to divide by.
   */
  const percent =
    questionnaire && (!submitted || questionnaire.completeness === 'complete')
      ? (questionnaire.answered / questionnaire.total) * 100
      : undefined;
  /**
   * ⚠️ A HARD STOP ENDS THE JOURNEY IN THE QUESTIONNAIRE, AND IT DOES NOT FLIP
   * THE ASSESSMENT STATE. CLIN-03: the engine stamps `eligibility` while the row
   * is still `draft`, and the assessment is sticky from that instant (every
   * further answer 409s). So the halt is keyed on the VERDICT plus the absence of
   * a submission — never on the state, which still reads `draft` on a stopped
   * record and `submitted` on a clinician's rejection much later.
   */
  const hardStopped = deal.eligibility === 'not_eligible' && assessment?.submittedAt == null;
  /**
   * ⚠️ THE TICK MEANS FINISHED, NOT STARTED, AND ONLY THE SUBMISSION PROVES IT.
   *
   * `reached: assessment != null` once put a green tick beside the line "3 of 6
   * — stopped at Q3". The replacement, `submitted || stoppedAt === null`, was
   * wrong in a quieter and more dangerous way: `stoppedAt` is derived from the
   * SERVED list and `total` from the BANK, and R28 redaction makes those two
   * differ, so a patient who had answered both questions they had been shown of
   * a fourteen-question bank ticked green under the words "All 14 questions
   * answered" and a 14% bar, directly below a band saying they had not finished.
   *
   * SO THE AUTHORITY IS `submittedAt`, EVERYWHERE AND ALONE. It is the server's
   * own statement that this form cleared `computeSubmission`; the count is a
   * derived, sometimes partially-visible view of the same form. The count writes
   * the WORDS and the SUBMISSION draws the MARKER — never the other way round,
   * which also makes this rung and the action band (which reads the same field)
   * incapable of disagreeing.
   *
   * ⚠️ AND A COMPLETE COUNT DOES NOT PROMOTE IT EITHER. A form with every answer
   * in and no submission is a real state — the last answer saved and the submit
   * never landed — and it is OUTSTANDING: clinic-service will not review it, so
   * a tick would tell a clinician a form was finished that the service does not
   * accept as finished. The note says "All 14 questions answered — not submitted
   * yet" and the rung keeps its ringed dot.
   */
  rungs.push({
    id: 'questionnaire',
    label: 'Questionnaire',
    reached: submitted,
    // The instant they STARTED answering — a `now` rung with a date is right
    // here: it says when this step began, not when it finished.
    at: assessment?.createdAt ?? null,
    note,
    percent,
    halt: started && hardStopped,
  });

  /**
   * THE WEIGHT PHOTO — RENDERED ONLY WHERE THERE IS SOMETHING TO SAY, and that
   * conditional is the whole point of this block.
   *
   * ⚠️ q14 IS NOT A PHOTO QUESTION ANY MORE. clinic-service replaced the
   * mandatory upload with a weight SELF-CERTIFICATION on 2026-09-04 (Reneal
   * Patel, in writing): requiring an image from every patient at the start had
   * taken throughput from ~25 leads a week to ~2. Evidence is now requested when
   * a clinician wants it, and photos can arrive at any time down three separate
   * lanes. So an always-present "Weight photo" rung would sit permanently dashed
   * on every current journey and read as an outstanding step that does not
   * exist — the exact bottleneck the change was made to remove, re-created in
   * the UI.
   *
   * It therefore appears when, and only when, the record has photo activity: an
   * image on file (the older mandatory lane, or evidence obtained since), or a
   * chase that went out. A chase with nothing back IS the actionable case, and
   * it is the reason this rung is worth having at all.
   *
   * ⚠️ NO DATE, AND THAT IS THE SERVER'S GAP, NOT A CHOICE. `AssessmentPhotoEntry`
   * carries `photoUrl`, `photoId`, `addedByRole` and `remainingDays` — no upload
   * instant. `remainingDays` could be arithmetic'd into one against a 365-day
   * retention window, and that would be a manufactured date on a clinical record.
   * THE UNBLOCK: an `uploadedAt` on the gallery entry.
   */
  const photos = assessment?.photos;
  const chases = assessment?.photoChaseCount ?? 0;
  if ((photos && photos.length > 0) || chases > 0) {
    const onFile = photos?.length ?? 0;
    rungs.push({
      id: 'weight_photo',
      label: 'Weight photo',
      reached: onFile > 0,
      at: null,
      note:
        onFile > 0
          ? `${onFile} on file${chases > 0 ? ` · chased ${chases} ${chases === 1 ? 'time' : 'times'}` : ''}`
          : `Chased ${chases} ${chases === 1 ? 'time' : 'times'} — nothing uploaded`,
    });
  }

  /**
   * CONSENT (q15).
   *
   * ⚠️ REACHED IS NOT `consentAgreedAt != null`. The field is optional on the
   * wire (a clinic-service that predates it omits it) and null when no row was
   * recorded — and a SUBMITTED assessment necessarily passed the consent gate,
   * because `computeSubmission` 400s `consent_required` without every consent
   * question ticked. Keying the tick on the timestamp alone would print a red
   * "never consented" on every journey older than the column, which is a false
   * claim about a patient's agreement. So the FACT comes from the submission and
   * the DATE comes from the column, independently — a tick with an em-dash where
   * we know it happened and cannot say when.
   */
  rungs.push({
    id: 'consent',
    label: 'Consent',
    reached: assessment?.consentAgreedAt != null || assessment?.submittedAt != null,
    at: assessment?.consentAgreedAt ?? null,
  });

  rungs.push({
    id: 'submitted',
    label: 'Submitted for review',
    reached: assessment?.submittedAt != null,
    at: assessment?.submittedAt ?? null,
  });

  return rungs;
}

/**
 * WHAT THEY CAME FOR — the qualification answer, as its own group (CTO,
 * 2026-09-13: "in the timeline we can have another section like questionnaire
 * for intent qualification").
 *
 * Returns null when there is nothing honest to show.
 *
 * ⚠️ THE RUNG IS ALWAYS `reached: true`, AND THAT IS THE WHOLE DESIGN. This
 * group is SECOND on the rail, ahead of everything a patient actually does, so
 * an unreachable rung here would take the single `now` marker on every journey
 * in the system — the same trap the S-2 note was written as a group NOTE to
 * avoid, spelled out over `arrival`. The group only appears when we have an
 * answer, and an answer is by definition already given.
 *
 * ⚠️ NOT CALLED "INTENT" ON ANY SURFACE. The CTO asked for that on the customer
 * side; this rail is staff-only (it is mounted inside the deal drawer, behind
 * ADMIN and CLINICIAN roles, and the patient portal renders no timeline at
 * all), so the constraint does not literally bind here. It is taken anyway:
 * `leadReadiness.ts` already made "intent" a banned CONCEPT name portal-wide
 * rather than a banned patient string, and a deal drawer is the patient's own
 * personal data — routinely screenshotted into tickets and reachable by a
 * subject-access request. Sales jargon about a named patient on a record they
 * can ask to see is a liability with no upside.
 */
function qualificationRung(input: JourneyInput): Rung | null {
  const intent = input.deal.intent;
  // `undefined` = this build of clinic-service does not send the field, so we
  // did not look. `null` = not answered: skipped before the screen became
  // required (2026-09-25), left before answering, came in by a lane that never
  // shows the screen, or the card predates the question. None of those is an
  // answer, and none earns a group.
  if (intent === undefined || intent === null) return null;
  const label = READINESS_STAFF_LABELS[intent as LeadReadinessKey];
  // An answer this build does not recognise is still an answer — say that,
  // rather than dropping the group and implying nothing was asked.
  return {
    id: 'qualification',
    label: label ?? 'Answered',
    reached: true,
    at: null,
  };
}

/** THE CLINICAL DECISION — one rung, three faces. */
function decisionRung(input: JourneyInput): Rung {
  const { deal, order, outcome } = input;
  const status = clinicalStatusFor(deal);
  const lane = journeyLane(deal.entryPath);
  /**
   * ON THE CLINICIAN-LED LANE THE WORD IS "RECOMMENDED" (CTO). The clinician on
   * a call recommends a treatment and the patient then chooses and pays; on the
   * self-serve lane the same act reads as an approval of what they submitted.
   * Same server fact, two honest sentences.
   */
  const approvedLabel = lane === 'clinician_led' ? 'Clinician recommended' : 'Clinician approved';

  /**
   * THE APPROVAL INSTANT — a real one since 2026-09-11 (clinic-service #199),
   * which is what this comment used to ask for by name.
   *
   * ⚠️ `planLinkLastSentAt` IS STILL NOT ONE, and that trap has not moved: it is
   * the NEWEST send row, so it advances every time a desk re-sends the plan
   * link. Dating a clinical decision from it would silently re-date the
   * decision to the last time somebody chased the patient.
   *
   * THE FALLBACK IS THE OLD ANSWER, KEPT FOR AN OLDER SERVER — `stageEnteredAt`
   * while, and only while, the card sits in `plan_sent`: the handover hook is
   * what moves it there, so the stage's entry IS the approval instant for as
   * long as it rests in that lane. It is strictly worse than the column (it
   * vanishes when the card moves on, and re-dates if the card is ever re-staged)
   * and it retires when no deployed clinic-service can omit `approvedAt`.
   *
   * Note the server BACKFILLED this from the `recommendation_issued` trail, so
   * a null here means genuinely NOT APPROVED rather than "approved before we
   * started recording" — which is why the fallback is second, not first.
   */
  const approvedAt = deal.approvedAt ?? (deal.stage === 'plan_sent' ? deal.stageEnteredAt : null);

  if (status === 'rejected') {
    return {
      id: 'decision',
      label: 'Clinician declined',
      reached: true,
      // The closure instant IS the decision instant on this lane, and unlike an
      // approval it is actually recorded — `outcome.closedAt`, the same field the
      // drawer's outcome block already renders. Null on an older clinic-service.
      at: outcome?.closedAt ?? null,
      halt: true,
    };
  }

  const plan = planWords(deal.paymentPosture);
  return {
    id: 'decision',
    label: approvedLabel,
    reached: status === 'approved',
    at: status === 'approved' ? approvedAt : null,
    /**
     * "WITH WHO AND WHAT" (CTO). The WHAT is here whenever a plan is on the
     * order — the medication and the term, money-free, so both roles read the
     * same words. The WHO is NOT: this aggregate records an approving clinician
     * nowhere (`outcome.closedByUserId` exists only for CLOSURES), and the
     * drawer's ADMIN-only staff lookup has no id to resolve. Naming the wrong
     * clinician on a prescribing decision is not a cosmetic error, so the rung
     * says nothing rather than guessing. THE UNBLOCK: an `approvedByUserId` beside
     * the `approvedAt` above.
     */
    note: status === 'approved' && plan ? plan : undefined,
    // A rejection reached by the ORDER lane rather than the assessment lane —
    // orders-service's own `rejected` state — stops the journey just as hard.
    halt: isTerminalOrderState(order?.state ?? null) && order?.state === 'rejected',
  };
}

/**
 * THE MONEY RUNGS, IN TWO GROUPS — and the split is not cosmetic.
 *
 * `hold` is everything the PATIENT does with their money before anyone decides
 * anything: choose a plan, give an address, save a card, authorise the first
 * month. `capture` is the one beat that happens AFTER the clinical decision —
 * `approve()` is what takes it, and it takes it automatically.
 *
 * ⚠️ THEY HAVE TO BE SEPARABLE BECAUSE THE PAID-FIRST LANE PUTS THE DECISION
 * BETWEEN THEM. Kept as one block, a paid-first card renders "Payment taken"
 * inside the phase called *Payment held*, ahead of the clinician it is waiting
 * on — so the rail marks the capture as the outstanding step while the action
 * band correctly says the clinician owes a decision. A rail and a band
 * disagreeing about whose move it is on the same screen is worse than either
 * being wrong alone.
 */
function paymentRungs(input: JourneyInput): { hold: Rung[]; capture: Rung } {
  const { deal, order, role } = input;
  const posture = deal.paymentPosture;
  const rungs: Rung[] = [];
  const plan = planWords(posture);

  rungs.push({
    id: 'plan_chosen',
    label: 'Plan chosen',
    reached: postureReached(posture, 'plan_only'),
    at: null,
    note: plan ?? undefined,
  });

  /**
   * DELIVERY DETAILS — RENDERED ONLY WHERE WE HAVE AN ORDER ROW TO READ IT FROM,
   * and that guard is doing real work rather than being defensive noise.
   *
   * The fact lives on the `StaffOrder`, which the drawer resolves only when the
   * card carries an `orderCaseRef` — and 0 of 36 `plan_sent` deals on production
   * carry one. An unconditional rung would therefore sit unreached on almost
   * every card in the pipeline, and because the rail marks the FIRST unreached
   * rung as the outstanding step, it would SWALLOW the "happening now" marker on
   * nearly every journey: a patient whose real next move is to open the card
   * form would be shown waiting on a delivery address nobody ever asked them for.
   *
   * ⚠️ NO ORDER ROW IS THE SAME KIND OF ABSENCE AS A NULL POSTURE: "we did not
   * look" is not "there is no address". Say nothing.
   *
   * ⚠️ NEVER THE ADDRESS ITSELF (R19). "We have one" is the journey fact; the
   * address is PII and already has a home on the Payments tab.
   */
  if (order) {
    rungs.push({
      id: 'delivery',
      label: 'Delivery details',
      reached: order.deliveryAddress != null,
      at: null,
    });
  }

  /**
   * ⚠️ TWO RUNGS, NOT ONE — see the module docblock. A saved card is a payment
   * METHOD; an authorisation is permission for THIS plan's money, and only the
   * second one lets `approve()` capture.
   */
  rungs.push({
    id: 'card_saved',
    label: 'Card saved',
    reached: postureReached(posture, 'card_on_file'),
    at: null,
  });

  /**
   * ⚠️ `priorMonth1` IS THE SAFETY CATCH AND IT BELONGS HERE. The ladder FALLS
   * BACK on a refund or a release, so a case we charged and repaid reads
   * `card_on_file` — identical to a patient who never paid a penny. Without this
   * note the two are indistinguishable on the one screen staff use to decide
   * whether to charge again.
   */
  const priorMonth1 = postureIsKnown(posture) ? posture.priorMonth1 : null;
  const authorisedNote =
    priorMonth1 === 'refunded'
      ? 'Month 1 was taken and REFUNDED — nothing is held now'
      : priorMonth1 === 'released'
        ? 'The month-1 hold lapsed and was released — nothing is held now'
        : undefined;

  // The HELD figure rides here while it is a hold — see `captureRung` for why
  // the one `authorisedPence` field annotates two different rungs.
  const holding = postureIsKnown(posture) && posture.state === 'authorised';
  const heldPence =
    role === 'ADMIN' && holding && typeof posture?.authorisedPence === 'number' ? posture.authorisedPence : null;
  rungs.push({
    id: 'authorised',
    label: 'Payment authorised',
    reached: postureReached(posture, 'authorised'),
    at: null,
    note: authorisedNote ?? (heldPence !== null ? `${formatPence(heldPence)} held` : undefined),
  });

  return { hold: rungs, capture: captureRung(input) };
}

/**
 * THE CAPTURE — the beat `approve()` performs, after the clinical decision.
 *
 * ⚠️ MONEY, ADMIN ONLY, AND IT ONLY GOES ON THE RUNG IT DESCRIBES.
 * `authorisedPence` is what is actually HELD OR TAKEN — one field covering two
 * different claims depending on where the ladder stands. Printing it under
 * "Payment taken" on an `authorised` case put "£188.65" beside a rung that had
 * NOT happened, which a desk reads as money we have. So the figure follows the
 * ladder: held → it annotates the authorisation, taken → it annotates the
 * capture, and neither rung carries it while it is describing the other one.
 *
 * The key does not exist on a clinician's posture at all, and this checks the
 * ROLE as well, so two independent things have to fail before a figure reaches
 * a money-free console.
 */
function captureRung(input: JourneyInput): Rung {
  const posture = input.deal.paymentPosture;
  const captured = postureIsKnown(posture) && posture.state === 'captured';
  const seesMoney = input.role === 'ADMIN' && postureIsKnown(posture);
  const pence = seesMoney && typeof posture?.authorisedPence === 'number' ? posture.authorisedPence : null;
  return {
    id: 'payment_taken',
    label: 'Payment taken',
    reached: captured,
    at: null,
    // The capture fires from the approval, not from a person pressing anything.
    by: 'auto',
    note: captured && pence !== null ? formatPence(pence) : undefined,
  };
}

/**
 * ⚠️ THE MONEY RUNGS WE ARE NOT ENTITLED TO DRAW.
 *
 * A dashed "Plan chosen / Card saved / Payment authorised" is not a neutral
 * shape — it is the CLAIM that none of those happened. That claim is only ours
 * to make when the server actually answered, and there are three states where it
 * did not: an ABSENT posture (a clinic-service predating the seam), a NULL one
 * (a mutation echo, which deliberately does not ask), and `{state:'unknown'}`
 * (orders-service unreachable). Rule 2 of the seam contract is explicit —
 * **never render `unknown` as "no card"** — and the first cut of this rail broke
 * it in the quietest possible way: the action band said "we do not know whether
 * this patient has a card", and eight dashed rungs underneath said they had not
 * saved one. The band and the rail contradicting each other about a patient's
 * money is worse than either being wrong on its own, because a desk believes the
 * picture over the paragraph.
 *
 * So the whole money chain collapses to ONE rung that says what we do not know.
 * It is the outstanding step, which is true: the outstanding thing IS finding
 * out. The wording splits the transport failure from the missing field, because
 * the two need different actions — go and look in Stripe, versus this build of
 * the API cannot tell us.
 */
function unknownMoneyRung(posture: DealPaymentPosture | null | undefined): Rung {
  return {
    id: 'payment_unknown',
    label: 'Payment status unavailable',
    reached: false,
    at: null,
    note:
      posture?.state === 'unknown'
        ? 'We could not reach the payment service. This is NOT "no card" — check before acting.'
        : 'This deal carries no payment information, so nothing here can be shown.',
  };
}

/**
 * PRESCRIPTION CREATION — the SECOND manual act of the clinical decision (CTO,
 * 2026-09-13). Returns null when this rung must not be shown at all.
 *
 * WHY IT IS ITS OWN RUNG. Approving and prescribing used to be one beat because
 * approval auto-issued the prescription. It does not any more: a clinician
 * approves, the money is captured, and the card comes BACK to them to prescribe
 * through the formulary. Two presses, by a person, on two different screens —
 * so two rungs. Collapsing them would hide the step that is actually
 * outstanding on every newly approved order.
 *
 * NO `by` TAG, DELIBERATELY. `by: 'auto'` is what the old supply rung wore, and
 * its absence here is the whole change: nothing issues a prescription on our
 * behalf any more.
 *
 * ⚠️ SHOWN ONLY ONCE A DECISION HAS BEEN MADE, AND ONLY WHEN THE FACT IS
 * READABLE. Two separate reasons, both load-bearing:
 *
 *  · Before approval, prescribing is not the outstanding move — the money is.
 *    This group is SCORED 4th, ahead of `hold`, so an always-present unreached
 *    rung here would take the single `now` marker off "Payment authorised" on
 *    the ordinary clinician-led lane and tell the desk a clinician owes a
 *    prescription when in fact the patient owes a card. A rejected case never
 *    gets one either.
 *
 *  · `awaitingPrescription` is `boolean | undefined`, and `undefined` means
 *    "this build of the API cannot tell" — not "no prescription". Saying
 *    nothing is the only honest render of that, the same rule the `delivery`
 *    rung follows for a missing order row.
 */
function prescriptionCreatedRung(input: JourneyInput): Rung | null {
  const { deal, order } = input;
  if (clinicalStatusFor(deal) !== 'approved') return null;
  // `undefined` = this build cannot tell. Say nothing rather than guess.
  if (order?.awaitingPrescription === undefined) return null;

  // A prescription ROW exists the moment the order stops awaiting one. That is
  // true even for the v1 backlog, whose rows record no prescriber-chosen
  // strength — the document was created, it is just not one we will dispense.
  // Reporting it as "not created" would be a different lie from the one this
  // change removes, so it reads as reached and SAYS what is wrong with it.
  const created = order.awaitingPrescription === false;
  const needsRework = order.needsPrescriptionReapproval === true;

  return {
    id: 'prescription_created',
    label: 'Prescription created',
    reached: created,
    // No instant is on this seam yet — `awaitingPrescription` is a boolean, not
    // a timestamp. An em-dash is honest; a borrowed date would not be. THE
    // UNBLOCK: `prescriptionIssuedAt` beside it on the payment-posture
    // projection, the same shape as the `approvedAt` unblock above.
    at: null,
    note: needsRework
      ? 'Created without a prescriber-chosen strength — it must be re-saved before the pharmacy can dispense it.'
      : undefined,
    // Approved and paid means a Won card, and a Won card is CLOSED. Without
    // this the rung would paint coral "Stopped here" on a patient who is simply
    // waiting for their clinician to finish.
    awaitingClinician: !created,
  };
}

/** THE SUPPLY CHAIN — everything `fulfilment.service.ts` runs off one approval. */
function supplyRungs(input: JourneyInput): Rung[] {
  const { deal, order } = input;
  const posture = deal.paymentPosture;
  // The POSTURE's order state first: it describes the SAME live case the band
  // and the pharmacy sign-in button read, and it is re-read with the deal. The
  // drawer's StaffOrder is fetched once, covers only the newest 200 orders and
  // can describe another case — it is the fallback, not the source.
  const state = postureIsKnown(posture) ? posture.orderState : (order?.state ?? null);

  /**
   * ⚠️ ORDER-STATE READING IS FORWARD-ONLY AND TOLERANT. `orderState` is a plain
   * string on the wire precisely so an orders deploy that names a state this
   * portal has never heard of does not fail a render. So each rung asks "is the
   * order at or past this beat?" against a KNOWN list, and an unrecognised state
   * reaches none of them — which renders an unfinished chain rather than a wrong
   * finished one.
   */
  const PAST_PHARMACY = ['awaiting_dispense', 'dispensed', 'dispatched'];

  /**
   * ⚠️ THERE IS NO "Prescription issued / AUTO" RUNG HERE ANY MORE, and its
   * removal is the point (CTO, 2026-09-13).
   *
   * It used to sit at the head of this list, reaching on a `PAST_APPROVAL`
   * list — which included `approved`. Since orders-service #236 deleted the auto-issue on
   * capture (2026-09-12), `approved` means CAPTURED AND NO PRESCRIPTION EXISTS.
   * So every newly approved order painted a green, AUTO-tagged "Prescription
   * issued" tick for a document that had never been created, on a clinical
   * record, while the action band said "Nothing for us to do" and a paid patient
   * waited for a clinician. A false positive about a prescription is exactly the
   * class of claim this rail must never make.
   *
   * Prescribing is now a MANUAL clinician act, so its rung belongs beside the
   * approval that precedes it — see `prescriptionCreatedRung` and the `decision`
   * group. This is a deliberate breach of the "no rung migrates between groups"
   * invariant below: the fact itself changed owner, from us to a person.
   */
  const rungs: Rung[] = [
    {
      id: 'pharmacy',
      label: 'Sent to pharmacy',
      reached: (state != null && PAST_PHARMACY.includes(state)) || order?.dispensedAt != null,
      at: null,
      by: 'auto',
      /**
       * THE PHARMACY'S OWN ANSWER, and a refusal STOPS the journey. A coarse ops
       * reason, not clinical prose — the patient is never told either way, and
       * the distinction from a clinician's rejection is exactly why
       * `dispense_rejected` wears DANGER in `tones.ts` while `rejected` is muted.
       */
      note: order?.dispenseRejectedReason
        ? `The pharmacy declined: ${order.dispenseRejectedReason.replace(/_/g, ' ')}`
        : order?.dispensedAt
          ? 'Dispensed'
          : undefined,
      halt: order?.dispenseRejectedReason != null,
    },
    {
      id: 'dispatched',
      label: 'Dispatched',
      reached: order?.dispatchedAt != null || state === 'dispatched',
      at: order?.dispatchedAt ?? null,
      // A PHARMACIST records this (courier + tracking) — never automatic.
      by: 'pharmacy',
      awaitingPharmacy: orderHasPharmacyWork(state),
      note: order?.courier
        ? `${order.courier}${order.trackingNumber ? ` · ${order.trackingNumber}` : ''}`
        : undefined,
    },
  ];

  /**
   * THE RECURRING PLAN — the final rung, and only on a TERM. A one-month order
   * has no recurring plan to be the last rung of, and rendering one would invent
   * a subscription.
   *
   * ⚠️ "MONTH 1 OF 3" IS AS FAR AS THIS CAN HONESTLY GO. The CTO asked for
   * "Month 1 of 3 · next bill 5 Oct", and the next-bill date is not on any read
   * this drawer makes: `dealCard.paymentPosture` carries the term and the
   * captured month, `StaffOrder` carries the fulfilment tail, and the
   * subscription reads (`getSubscriptionDetail`) are ADMIN-only, keyed on a
   * subscription id this surface never learns. A billing date is the single worst
   * field to guess — a patient rings about a charge on a date we printed and
   * nobody can explain it. THE UNBLOCK: a `nextBillAt` beside `months` on the
   * posture, which is one field on a seam that already crosses.
   */
  const months = postureIsKnown(posture) ? posture.months : null;
  if (months != null && months > 1) {
    const captured = postureIsKnown(posture) && posture.state === 'captured';
    rungs.push({
      id: 'recurring',
      label: 'Recurring plan active',
      reached: captured,
      at: null,
      // Month 1 is the only month this seam can see; the note says exactly that
      // rather than implying we are tracking the term's progress.
      note: captured ? `Month 1 of ${months}` : undefined,
    });
  }

  return rungs;
}

/* ══════════════════════════════════════════════════════════════════════════
 * PHASES
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ THE GROUP ORDER IS FIXED, AND IT IS NEVER A SEQUENCE CLAIM (2026-09-11).
 *
 * This replaces `isPaidFirst`, which swapped two groups to show which came
 * first. That mechanism answered two different questions with one output, and
 * the answers have different rules:
 *
 *   Q1  What happened first for this patient?   → the past tense. Durable
 *       instants ONLY, and often honestly "not recorded".
 *   Q2  What is this patient waiting for now?   → the present tense. Current
 *       state, freely, because it is a claim about now.
 *
 * A swapped layout answers Q1 by implication and gets read as Q2 (or the other
 * way round), and — the part that made it wrong rather than merely muddled — a
 * LAYOUT CANNOT SAY "WE DO NOT KNOW". It must pick an order, so for every card
 * whose sequence is unrecorded it asserts one. On a panel a clinician reads to
 * see what happened to a patient, that is a confident wrong answer.
 *
 * So Q1 moved into one sentence on the Clinical decision group (`sequenceNote`),
 * where "not recorded" is sayable, and the groups are SCORED in the same order
 * every time:
 *
 *   arrival → call → form → decision → hold → supply
 *
 * ⚠️ THAT IS THE SCORING ORDER, AND SINCE 2026-09-12 IT IS NO LONGER THE ORDER
 * A READER WALKS — see `NARRATIVE_ORDER`, which re-sequences the groups AFTER
 * they are scored so the rail reads like the route without moving the single
 * `now` marker. Everything below is about the marker, and none of it changed.
 *
 * THE SCORING ORDER IS CHOSEN FOR THE `now` MARKER, not for narrative. There is
 * a single marker on the rail and it lands on the first unreached rung, so the
 * order has to put it where the work actually is — and this one does, in every
 * live case, with no predicate:
 *   · both decision and money outstanding → marker on the decision rung. Right
 *     on both lanes: whenever both are open, the clinician owes the next move.
 *   · decided, no card yet (the post-reversal default) → decision `done`, the
 *     marker falls through to the first unreached money rung. Right.
 *   · questionnaire outstanding → marker lands in `form`, above both. Right.
 *   · paid-first card still in the queue → decision is `now` with ticked money
 *     rungs BELOW it, because `assignStates` still marks later reached rungs
 *     `done`. That reads as "waiting on the clinician; the card is already
 *     authorised" — which is exactly what could not be seen before.
 *
 * It also happens to match the nominal S-1..S-7 funnel after the 2026-09-10
 * reversal made decision-before-card the default. That is a coincidence worth
 * nothing: position is not evidence — which is exactly why the 2026-09-12
 * re-sequencing for readers was safe to do WITHOUT touching this list.
 *
 * GROUP TITLES CARRY NO NUMERALS. A header numbered 4 above one numbered 5,
 * with a sentence underneath saying we cannot tell which came first, is a
 * contradiction inside one viewport. The S-mapping lives in this comment.
 *
 * MEMBERSHIP IS FIXED AT RENDER TIME — no rung migrates between groups on any
 * per-card condition, and neither the 2026-09-11 regrouping nor the 2026-09-12
 * re-sequencing changed that. The old `supply` id carried two different titles
 * and two different rung sets depending on the swap, which is a trap for
 * anything keyed on a phase id.
 *
 * ⚠️ ONE RUNG HAS MOVED ONCE, IN SOURCE, AND THAT IS A DIFFERENT THING
 * (2026-09-13). "Prescription issued / AUTO" left `supply` and came back as
 * "Prescription created" in `decision`, because the fact underneath it changed
 * owner: orders-service stopped issuing prescriptions on capture, so a
 * clinician now does it by hand. The invariant forbids a rung whose GROUP
 * depends on the card in front of you; it has never frozen the rail against the
 * product changing shape. A rung that stayed in `supply` would have gone on
 * claiming that something automatic had happened.
 */

/** The S-2 capture gap, stated on the group where the question would be asked. */
const INTENT_NOT_CAPTURED_NOTE =
  'What they are looking to do today, and any treatment they are already on, is not captured anywhere on this record.';

/**
 * Q1 — WHICH CAME FIRST, THE CARD OR THE DECISION, in one sentence.
 *
 * Durable instants only: `holdAuthorisedAt` (2026-09-07) and `approvedAt`
 * (2026-09-11). Both are columns that survive the journey completing, which is
 * what the old current-state predicate could not manage.
 *
 * ⚠️ AND IT SAYS "NOT RECORDED" WHERE THAT IS THE TRUTH — the one thing the old
 * swapped layout could not do. Two populations need it: a card authorised
 * before 2026-09-07 whose hold was never re-stamped (that backfill caught only
 * `clinical_review` cards carrying an `orderCaseRef`), and any card where the
 * money arrived through a lane that left no instant. For those the posture
 * still reports the money, so we know it happened and not when — and inventing
 * an order from that is exactly the failure this rewrite exists to end.
 *
 * Returns undefined when there is no decision yet: with nothing to order, a
 * sentence about ordering is noise.
 */
function sequenceNote(deal: DealCard): string | undefined {
  const { holdAuthorisedAt, approvedAt } = deal;
  if (!approvedAt) return undefined;
  if (holdAuthorisedAt) {
    // `<=` for the same-instant pair: `approvePlan`'s charged lane captures in
    // the request it approves in, and that request captured a hold that was
    // already sitting there.
    return Date.parse(holdAuthorisedAt) <= Date.parse(approvedAt)
      ? 'The card was authorised before this decision.'
      : 'This decision came before any card was authorised.';
  }
  const posture = deal.paymentPosture;
  const moneyExists = postureIsKnown(posture) && isChargeablePosture(posture.state);
  return moneyExists
    ? 'Whether the card was authorised before this decision is not recorded.'
    : 'No card had been authorised when this decision was made.';
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE ACTION BAND
 * ════════════════════════════════════════════════════════════════════════ */

/**
 * WHEN THE PLAN LINK IS MEANINGFUL — the EXISTING gate, copied rather than
 * re-invented (`DealDrawer`'s old "Plan link" section used exactly this).
 * Before a clinician has approved, the URL is not meaningful: the handover 409s
 * and the patient has nothing to pay against.
 */
function planLinkAvailable(deal: DealCard, assessment: DealDetail['assessment']): boolean {
  if (!assessment || !deal.assessmentId) return false;
  return clinicalStatusFor(deal) === 'approved' || deal.planSent === true || deal.stage === 'plan_sent';
}

/**
 * ⚠️ WHEN THE STRIPE PAYMENT LINK MAY BE OFFERED — AND THIS GATE IS NARROWER
 * THAN "ADMIN", DELIBERATELY.
 *
 * `paymentLink()` mints a hosted Stripe page and returns. NOTHING creates a
 * PaymentRecord, activates a subscription or issues a prescription from a
 * payment-link payment — so a patient who pays through one before their first
 * capture has paid us with no order record, no plan and no prescription behind
 * it. It is not a rescue route for a pre-payment patient, and putting it in
 * front of one on the drawer would make that failure easy and routine.
 *
 * So the band offers it ONLY where it is the control it was built to be: an
 * ADMIN, on a plan whose month 1 is ALREADY `captured` — a running plan whose
 * outstanding money is a renewal, which is precisely the failed-payments board's
 * own case. Everything earlier gets the PLAN link instead, which walks the
 * patient through the real checkout that records everything.
 *
 * The route itself stays `@Roles('ADMIN')` server-side; this is affordance.
 */
function paymentLinkAvailable(input: JourneyInput): boolean {
  const posture = input.deal.paymentPosture;
  if (input.role !== 'ADMIN') return false;
  if (!postureIsKnown(posture)) return false;
  if (isTerminalOrderState(posture.orderState)) return false;
  return posture.state === 'captured' && posture.orderCaseRef != null;
}

/**
 * ⚠️ IS THIS A PAID ORDER THE DISPENSARY HAS NEVER BEEN SENT? (2026-09-11 —
 * incident 2026-09-09.)
 *
 * A capture and a pharmacy send are separate events. orders-service banks the
 * money and THEN issues the prescription and posts it to the dispensary,
 * post-commit and best-effort, precisely so that nothing in that tail can unwind
 * a charge — which means the send can fail behind a capture that succeeded.
 * `recordPharmacySendWithinTx` flips `approved → awaiting_dispense` ONLY on a
 * confirmed `PharmacySend.sentAt`, so a `captured` posture whose order is still
 * at `approved` is a paid order no pharmacy has heard of.
 *
 * On 2026-09-09 that lasted two days, for a real patient, on production. She
 * checked out as a GUEST, so identity holds no `Patient` row for her, so the
 * dispensing request carried no name and no date of birth, and the send was
 * refused — correctly, because a pharmacist cannot verify a POM against "Not
 * recorded". Every surface she appeared on read as finished.
 *
 * ⚠️ TWO GUARDS, AND NEITHER IS OPTIONAL:
 *
 *  · `postureIsKnown` — the three absences. A missing posture, a mutation echo
 *    and an `unknown` transport failure all answer FALSE. We could not learn
 *    whether this patient was charged, so we are not entitled to claim their
 *    order is stuck; and a warning that fired on every failed posture read would
 *    be ignored inside a week, which is the same outcome as not having one.
 *  · `isTerminalOrderState` — a REJECTED or CANCELLED order owes no pharmacy
 *    send. Those are the refund lane's business, and flagging them as "not sent"
 *    would point an operator at a dispensary for an order that was correctly
 *    never dispensed.
 *
 * `orderReachedPharmacy` carries the third: `dispense_rejected` counts as
 * REACHED, because that pharmacy did receive the prescription and refused it.
 *
 * ⚠️ IT IS TRUE OF AN ORDER NOBODY HAS PRESCRIBED YET, AND THAT IS WHY THE BAND
 * READS CASE 0a FIRST (orders-service #236, 2026-09-12). `approve()` no longer
 * auto-issues a prescription, so the ordinary resting place of a freshly approved
 * order is paid, at `approved`, with nothing prescribed — which is also "the
 * dispensary has not been told". A CLINICIAN owes that one, not an admin, and
 * `clinicianOwesPrescription` above says so in the right words. This predicate is
 * deliberately NOT given a second copy of that test: a guard in here would be
 * unreachable behind 0a, and an unreachable guard is one no mutation can prove.
 * What holds is the ORDER of the two cases, which
 * journeyPanel.spec.tsx §4 pins directly ("a clinician who has NOT prescribed yet
 * is NOT an admin fulfilment stall") — swap them and that test fails.
 *
 * R41: every input is a server fact already on the card. Nothing here is fetched
 * and nothing is inferred — this asks the same question orders-service answers
 * when it flips the state, rather than inventing a second definition of "the
 * pharmacy has it".
 */
function paidButPharmacyNotTold(posture: DealPaymentPosture | null | undefined): boolean {
  if (!postureIsKnown(posture)) return false;
  if (posture.state !== 'captured') return false;
  if (isTerminalOrderState(posture.orderState)) return false;
  return !orderReachedPharmacy(posture.orderState);
}

/**
 * What the stall band SAYS — two sentences, because we do not always know the
 * same amount.
 *
 * With `awaitingPrescription === false` the server has told us a prescription
 * exists, so the sentence names both halves: charged, prescribed, not sent. That
 * is the 2026-09-09 shape exactly.
 *
 * With the key absent we know the money moved and the dispensary has not been
 * told, and nothing about a prescription — so the copy says only that, and sends
 * the reader to the order rather than asserting a document we cannot see. A band
 * that over-states its evidence is how the first false sentence got believed.
 */
function pharmacyNotToldDetail(order: JourneyInput['order']): string {
  return order?.awaitingPrescription === false
    ? 'The charge went through and the prescription was issued; the dispensing request did not. ' +
        'Nothing is moving until somebody resends it — open the order and check it.'
    : 'The charge went through and the dispensary has not been sent this order. ' +
        'Open the order to see what is on file, and resend it.';
}

/**
 * The band. ORDER MATTERS — the first case that applies wins, and they are
 * arranged from "the journey is over" backwards through the funnel, so a later,
 * more specific stage can never be masked by an earlier generic one.
 */
/**
 * A CLINICIAN OWES A PRESCRIPTION ON THIS ORDER RIGHT NOW — or null when this
 * API build cannot tell, which is NOT the same as "no prescription is owed".
 *
 * Returns the sentence to say, so the band has exactly one place to learn both
 * the fact and its wording. The rail learns the same fact in
 * `prescriptionCreatedRung`; a band contradicting the rail inside one viewport
 * is the failure this module already names as worse than either being wrong
 * alone, because a desk believes the picture over the paragraph.
 */
function clinicianOwesPrescription(input: JourneyInput): string | null {
  const order = input.order;
  if (!order) return null;
  if (order.awaitingPrescription === true) {
    return 'Next: a clinician prescribes. The pharmacy has not been sent anything yet.';
  }
  if (order.needsPrescriptionReapproval === true) {
    return 'Next: a clinician re-saves the prescription — the one on file records no chosen strength.';
  }
  return null;
}

function buildBand(input: JourneyInput, closed: boolean): JourneyBand {
  const { deal, assessment, role } = input;
  const posture = deal.paymentPosture;
  const status = clinicalStatusFor(deal);
  const actions = {
    planLink: planLinkAvailable(deal, assessment),
    paymentLink: paymentLinkAvailable(input),
  };
  const plan = planWords(posture);
  const seesMoney = role === 'ADMIN' && postureIsKnown(posture);

  /**
   * 0a. A CLINICIAN OWES A PRESCRIPTION — read BEFORE the closed branch, which
   * is exactly the ordering rule stated above: a later, more specific stage
   * must never be masked by an earlier generic one.
   *
   * ⚠️ WITHOUT THIS, THE MOST COMMON POST-APPROVAL CARD LIES TWICE OVER. An
   * approved, paid order sits on a Won card, and Won is closed — so branch 1
   * answered "Nothing outstanding — this deal is closed" for a patient who has
   * paid and is waiting for a prescription nobody has written. Until
   * orders-service #236 that was true, because capture auto-issued the
   * prescription; since 2026-09-12 the card comes back to the clinician and
   * the claim became false. The rail has the same carve-out for the pharmacy
   * (`awaitingPharmacy`) for the same reason: a Won card is not a finished one.
   */
  const owedPrescription = clinicianOwesPrescription(input);
  if (owedPrescription) {
    return {
      tone: 'amber',
      owner: 'CLINICIAN',
      headline: owedPrescription,
      detail: plan,
      // A closed deal keeps no link controls, and this card is usually closed;
      // the outstanding move is a prescription, not a link to the patient.
      actions: { planLink: false, paymentLink: false },
    };
  }

  /* 0b. ⚠️ PAID, THE PRESCRIPTION EXISTS, AND THE PHARMACY WAS NEVER TOLD —
   *     ABOVE THE CLOSED GUARD, AND THAT PLACEMENT IS THE WHOLE FIX
   *     (incident 2026-09-09).
   *
   * ⚠️ READ AFTER 0a, AND THE ORDER IS LOAD-BEARING — IT IS THE ONLY THING
   * SEPARATING THE TWO. Since orders-service #236 deleted the auto-issue on
   * capture, the ordinary state of a freshly approved order is paid, at
   * `approved`, with NO prescription yet, which satisfies this case's condition
   * word for word. Reading it first would tell an ADMIN that a dispensing request
   * had failed on every newly approved order in the book, and say "the
   * prescription was issued" about orders that have none: a new false sentence in
   * place of the old one, on the most common card in the business. Swapping these
   * two blocks fails journeyPanel.spec.tsx §4's "a clinician who has NOT
   * prescribed yet is NOT an admin fulfilment stall".
   *
   * This band's rule is stated two comments up: "arranged from 'the journey is
   * over' backwards through the funnel, so a later, more specific stage can
   * never be masked by an earlier generic one." A withheld dispensing send is
   * exactly such a fact, and case 1 was masking it.
   *
   * An approved, paid order moves its deal to `closed_won` — which is in
   * TERMINAL_STAGES, so the band short-circuited to neutral, no owner,
   * "Nothing outstanding — this deal is closed." That is the sentence the
   * incident patient's drawer actually carried while her £192.50 sat charged and
   * her prescription sat unsent. It is the strongest possible statement that
   * there is no work here, printed over the most urgent work in the business.
   *
   * `closed_won` means WE ARE DONE SELLING. It has never meant the medicine
   * shipped, and the one fact allowed to outlive the closure is the one where we
   * took a patient's money and the treatment did not move.
   *
   * AMBER AND OWNED, not red: the money is safe and the prescription is real —
   * this is work to pick up, not a clinical emergency. `owner: 'ADMIN'` because
   * the clinician's part is finished and there is no clinical decision left to
   * make; leaving it null would repeat the original failure in a new colour,
   * since a band with no owner is a band nobody picks up.
   *
   * NO LINK CONTROLS, matching case 1: this patient has paid, so emailing them a
   * plan link or a payment link is the wrong move in both directions.
   */
  if (paidButPharmacyNotTold(posture)) {
    return {
      tone: 'amber',
      owner: 'ADMIN',
      headline: 'Paid, but the pharmacy has NOT been sent this prescription.',
      detail: pharmacyNotToldDetail(input.order),
      actions: { planLink: false, paymentLink: actions.paymentLink },
    };
  }

  /* 1. NOTHING IS OUTSTANDING BECAUSE THE DEAL IS OVER. */
  if (closed) {
    return {
      tone: 'neutral',
      owner: null,
      headline: 'Nothing outstanding — this deal is closed.',
      detail: deal.closedReason?.trim() ? `Closed as: ${deal.closedReason.trim()}` : null,
      // A closed deal keeps NO link controls: emailing a plan link to a patient
      // whose record has been closed is a contact we cannot stand behind.
      actions: { planLink: false, paymentLink: false },
    };
  }

  /* 2. WE ASKED AND COULD NOT LEARN. Rule 2 of the seam: never route `unknown`
   *    as "no card". This band exists to stop a desk acting on a silence. */
  if (posture != null && posture.state === 'unknown') {
    return {
      tone: 'amber',
      owner: 'ADMIN',
      headline: 'Next: check this patient in Stripe before doing anything with their money.',
      detail:
        'We could not reach the payment service, so we do not know whether this patient has a card or an authorisation. This is NOT "no card" — treat it as unknown.',
      actions: { planLink: actions.planLink, paymentLink: false },
    };
  }

  /* 3. NO QUESTIONNAIRE YET. */
  if (!assessment) {
    const lane = journeyLane(deal.entryPath);
    return {
      tone: 'amber',
      owner: lane === 'clinician_led' ? 'CLINICIAN' : 'PATIENT',
      headline:
        lane === 'clinician_led'
          ? 'Next: start the questionnaire with the patient — nothing has been filled in yet.'
          : 'Next: the patient has not started the questionnaire.',
      detail: null,
      actions,
    };
  }

  /* 4. THE FORM IS UNFINISHED.
   *
   * ⚠️ THE SAME FIELD THE RUNG READS (`submittedAt`) AND THE SAME SENTENCE THE
   * RUNG PRINTS. The band cannot say "must finish" over a ticked questionnaire,
   * or state a different tally from the one on the rail, because neither is
   * derived twice — see `questionnaireLine`. The old second derivation ended
   * "2 of 14 answered." beside a rung reading "All 14 questions answered". */
  if (assessment.submittedAt == null) {
    const q = input.questionnaire;
    return {
      tone: 'amber',
      owner: 'PATIENT',
      headline: 'Next: the patient must finish the questionnaire.',
      detail: q ? `${questionnaireLine(q, false)}.` : null,
      actions,
    };
  }

  /* 5. SUBMITTED, AWAITING THE CLINICAL DECISION.
   *
   * ⚠️ THE TONE TURNS ON WHETHER APPROVING WILL ACTUALLY COMPLETE ANYTHING, not
   * on whose move it is. With a held authorisation the approval captures and the
   * automatic chain runs, so the band is GREEN and says so. Without one the same
   * press only emails a plan link — a real and useful thing, and NOT the same
   * thing — so it is AMBER and says that instead. A clinician told "we charge and
   * dispatch" on a patient with no card would believe the money was handled. */
  if (status !== 'approved') {
    const chargeable = postureIsKnown(posture) && isChargeablePosture(posture.state);
    if (chargeable) {
      const money =
        seesMoney && typeof posture?.authorisedPence === 'number'
          ? ` · ${formatPence(posture.authorisedPence)} authorised`
          : '';
      return {
        tone: 'green',
        owner: 'CLINICIAN',
        headline: 'Next: clinician approves → we charge and dispatch.',
        detail: `Card on file. ${plan ?? 'Plan on the order'}${money}.`,
        actions,
      };
    }
    return {
      tone: 'amber',
      owner: 'CLINICIAN',
      headline: 'Next: a clinician reviews the answers.',
      detail:
        'No payment is authorised, so approving emails the patient a plan link — it does not take any money.',
      actions,
    };
  }

  /* 6. APPROVED. What happens next is entirely about the money. */
  if (!postureIsKnown(posture)) {
    /* Absent (an older clinic-service) or null (a mutation echo). Say nothing
       about the card either way — the third absence rule. */
    return {
      tone: 'amber',
      owner: 'PATIENT',
      headline: 'Next: the patient chooses a plan and pays.',
      detail: 'This deal carries no payment information, so we cannot say what the patient has done since.',
      actions,
    };
  }

  if (posture.state === 'captured') {
    const dispatched = input.order?.dispatchedAt != null || posture.orderState === 'dispatched';

    /**
     * ⚠️ CAPTURED IS NOT "OVER TO THE PHARMACY" ANY MORE (2026-09-13). Until
     * orders-service #236 the capture auto-issued the prescription, so money
     * taken really did mean the pharmacy held it and there genuinely was
     * nothing for us to do. Since that release the card comes BACK to the
     * clinician, and this branch went on saying, in green, "Next: the pharmacy
     * dispenses and dispatches. Nothing for us to do." — on an order the
     * pharmacy has never seen, while a paid patient waited.
     *
     * The rail says a clinician owes the prescription (see
     * `prescriptionCreatedRung`). A band contradicting the rail inside one
     * viewport is the failure this module already names as worse than either
     * being wrong alone, and a desk believes the picture over the paragraph.
     *
     * `undefined` still falls through to the old wording: it means this API
     * build cannot tell, which is not evidence that a prescription is owed.
     */
    return {
      tone: 'green',
      // NOBODY'S MOVE. The chain from here is `fulfilment.service.ts` and the
      // pharmacy; naming an owner would put a job on a desk that has none.
      owner: null,
      headline: dispatched
        ? 'Nothing outstanding — the treatment has been dispatched.'
        : 'Next: the pharmacy dispenses and dispatches. Nothing for us to do.',
      detail: plan,
      actions,
    };
  }

  if (posture.state === 'authorised') {
    const money =
      seesMoney && typeof posture.authorisedPence === 'number'
        ? ` · ${formatPence(posture.authorisedPence)} authorised`
        : '';
    return {
      tone: 'green',
      owner: 'CLINICIAN',
      headline: 'Next: clinician approves the order → we charge and dispatch.',
      detail: `Card on file. ${plan ?? 'Plan on the order'}${money}.`,
      actions,
    };
  }

  /* 7. APPROVED AND THE MONEY NEVER LANDED — the four rungs below `authorised`,
   *    each of which needs a DIFFERENT sentence from a desk chasing the patient. */
  const priorMonth1 = posture.priorMonth1;
  const refundNote =
    priorMonth1 === 'refunded'
      ? ' Month 1 was taken and refunded, so this would be a second charge.'
      : priorMonth1 === 'released'
        ? ' The month-1 hold lapsed and was released.'
        : '';

  if (posture.state === 'card_on_file') {
    return {
      tone: 'amber',
      owner: 'PATIENT',
      headline: 'Next: the patient must finish checkout — a card is saved but nothing is authorised.',
      detail: `A saved card is not permission to charge: approving now would fail.${refundNote}`,
      actions,
    };
  }
  if (posture.state === 'checkout_started') {
    return {
      tone: 'amber',
      owner: 'PATIENT',
      headline: 'Next: the patient must finish checkout — they opened the card form and never saved a card.',
      detail: plan ? `They had chosen ${plan}.${refundNote}` : refundNote.trim() || null,
      actions,
    };
  }
  // `plan_only` and `no_order` — the two rungs where nothing was ever collected.
  return {
    tone: 'amber',
    owner: 'PATIENT',
    headline: 'Next: the patient must choose a plan and pay — no card was ever collected.',
    detail:
      posture.state === 'plan_only'
        ? `They started an order and never opened the card form.${refundNote}`
        : 'They have not started an order at all — this is the ordinary state before checkout.',
    actions,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE WHOLE PANEL
 * ════════════════════════════════════════════════════════════════════════ */

/** Build everything the Patient journey panel renders, from facts the drawer already holds. */
export function buildJourney(input: JourneyInput): Journey {
  const { deal, callback, activity } = input;
  const lane = journeyLane(deal.entryPath);
  /**
   * CLOSED means "no work left here", which is `TERMINAL_STAGES` — the portal
   * mirror of the server's own list. It is deliberately NOT "did we lose this
   * patient": a `closed_won` deal is terminal and is not a loss, and the band
   * says "nothing outstanding" for both.
   */
  const closed = TERMINAL_STAGES.includes(deal.stage);

  const arrived: Rung = { id: 'arrived', label: 'Arrived', reached: true, at: deal.createdAt };

  const form = formRungs(input);
  const qualification = qualificationRung(input);
  const decision = decisionRung(input);
  const prescriptionCreated = prescriptionCreatedRung(input);
  const { hold, capture } = paymentRungs(input);
  const supply = supplyRungs(input);

  /**
   * ⚠️ See `unknownMoneyRung`. With no answer from the seam the money chain is
   * one honest rung instead of eight dishonest ones — and the SUPPLY chain goes
   * with it unless there is a real `StaffOrder` to read, because `orderState`
   * came from the same place the posture did.
   */
  const moneyIsKnown = postureIsKnown(deal.paymentPosture);
  const holdRungs = moneyIsKnown ? hold : [unknownMoneyRung(deal.paymentPosture)];
  const captureRungs = moneyIsKnown ? [capture] : [];
  const supplyChain = moneyIsKnown || input.order ? supply : [];

  const plan: PhasePlan[] = [];

  // ── 1. ARRIVAL (S-1) — always, and always the head of the rail ────────────
  // `arrived` is the only rung whose `reached` is a literal `true`, so keeping
  // it first means the first-unreached scan never starts at index 0 and the
  // rail always has at least one `done` rung.
  //
  // The S-2 gap rides here as a group NOTE rather than a rung. An unreachable
  // rung at index 1 would take the single `now` marker on every journey in the
  // system — the panel would tell every reader that every patient is waiting on
  // a question we do not ask.
  plan.push({
    id: 'arrival',
    title: 'How they reached us',
    owner: 'Patient',
    // ⚠️ THE S-2 GAP NOTE IS NOW CONDITIONAL. It states, flatly, that what they
    // are looking to do "is not captured anywhere on this record" — which became
    // a FALSE claim on a clinical record the moment the qualification screen
    // started recording an answer. It speaks only while there is genuinely
    // nothing to show, and the group below carries the answer when there is.
    note: qualification ? undefined : INTENT_NOT_CAPTURED_NOTE,
    rungs: [arrived],
  });

  // ── 1b. WHAT THEY CAME FOR — only when they told us ──────────────────────
  // Omitted entirely rather than shown empty: a header with no rung is itself a
  // claim that a stage exists and was not reached, and "we did not ask" and
  // "they skipped it" are both already covered by the note above.
  if (qualification) {
    plan.push({
      id: 'qualification',
      title: 'What they came for',
      owner: 'Patient',
      rungs: [qualification],
    });
  }

  /**
   * ── 2. THE CALL (S-3, callback route) — when there was one ────────────────
   *
   * ⚠️ GATED ON THE EVIDENCE, NOT ON THE LANE, and that is a real fix rather
   * than a tidy-up. `staff_sql` is a `clinician_led` lane and creates NO
   * callback ticket, so a lane-gated group put a permanently unreachable "Call
   * completed" rung at rail index 1 on every staff-created card — and the
   * single `now` marker landed on it, for ever.
   *
   * THE ONLY CALL INSTANT ANYWHERE IS THE LOGGED ATTEMPT. The ticket carries a
   * status, a preferred window and two counters — no completion timestamp — so
   * the date is the newest `call_attempt` row a human actually logged, and an
   * em-dash where nobody logged one. `reached` is the ticket being CLOSED, or
   * the patient having an assessment at all: a form in front of us is proof the
   * call did its job, whatever state the ticket was left in.
   */
  const callEvidenced = callback != null || activity.some((entry) => entry.kind === 'call_attempt');
  if (callEvidenced) {
    const callDone = callback?.status === 'closed' || input.assessment != null;
    plan.push({
      id: 'call',
      title: 'Their call',
      owner: 'Clinician',
      rungs: [
        {
          id: 'call_completed',
          label: 'Call completed',
          reached: callDone,
          at: lastCallAttemptAt(activity),
          note:
            callback && callback.attempts > 0
              ? `${callback.attempts} attempt${callback.attempts === 1 ? '' : 's'}`
              : undefined,
        },
      ],
    });
  }

  // ── 3. THEIR FORM (S-3) — always ──────────────────────────────────────────
  plan.push({ id: 'form', title: 'Their form and consents', owner: 'Patient', rungs: form });

  /**
   * ⚠️ A JOURNEY THAT STOPPED HERE OWNS NOTHING BELOW, and the owner captions
   * go quiet for the rest of the rail.
   *
   * The case that forced this: an ENGINE hard stop. `clinical-status-chip`'s
   * spec 4b asserts the bare word "Clinician" appears NOWHERE in the drawer for
   * such a card, because an engine decision must never be attributed to a
   * human — and a caption reading "Clinician" over the Clinical decision group
   * of a case no clinician will ever see is precisely that attribution.
   *
   * It is the right rule beyond that one spec: a caption says whose step a
   * stage is, and on a journey that ended without a clinical decision the later
   * stages are nobody's — there is no step for anyone to own.
   */
  const decisionWillNeverHappen =
    // The engine stopped the journey in the form — the halt is on that rung, so
    // read it there rather than re-deriving the stop condition.
    form.some((rung) => rung.halt === true) ||
    // Or the card is terminal with no clinical decision on it: closed for a
    // non-clinical reason, merged as a duplicate, or lost to the safety stop
    // list. `closed` is TERMINAL_STAGES, so a `closed_won` card — which has a
    // decision — is untouched, and so is every open card.
    (closed && !decision.reached);
  const ownerUnlessStopped = (owner: string): string | undefined =>
    decisionWillNeverHappen ? undefined : owner;

  // ── 4. CLINICAL DECISION (S-5) — always, and it carries Q1 ────────────────
  // ⚠️ 4 AND 5 ARE SCORING POSITIONS, NOT READING POSITIONS. A self-serve reader
  // sees this group AFTER the money one (`NARRATIVE_ORDER`); it is scored here
  // so the `now` marker still lands on the decision while a clinician owes it.
  // The sentence is the ONLY place this panel states which came first for THIS
  // patient, and it outranks the reading order. See `sequenceNote`.
  // TWO MANUAL ACTS, NOT ONE (CTO, 2026-09-13): the clinician approves, and then
  // — on a second screen, after the money is captured — the clinician creates
  // the prescription. `prescriptionCreatedRung` returns null until a decision
  // exists and the fact is readable, so this is a one-rung group until then.
  plan.push({
    id: 'decision',
    title: 'Clinical decision',
    owner: ownerUnlessStopped('Clinician'),
    note: sequenceNote(deal),
    rungs: [decision, ...(prescriptionCreated ? [prescriptionCreated] : [])],
  });

  // ── 5. PLAN, DELIVERY AND CARD (S-4) — always ─────────────────────────────
  // Read BEFORE the clinical decision on the self-serve lane and after it on the
  // clinician-led one — the lane-keyed table, not this push order, decides that.
  // Collapses to the single honest rung when the money seam gave no answer
  // (`unknownMoneyRung`), rather than showing eight rungs derived from nothing.
  plan.push({
    id: 'hold',
    title: 'Plan, delivery and card',
    owner: ownerUnlessStopped('Patient'),
    rungs: holdRungs,
  });

  // ── 6. CHARGE AND SUPPLY (S-6 + S-7) — when there is any ─────────────────
  // OMITTED ENTIRELY when empty, header and all. An empty header is itself a
  // claim that a stage exists and was not reached, and on a card with no money
  // answer at all we do not know that.
  const supplyGroupRungs = [...captureRungs, ...supplyChain];
  if (supplyGroupRungs.length > 0) {
    plan.push({
      id: 'supply',
      title: 'Charge and supply',
      owner: ownerUnlessStopped('Us and the pharmacy'),
      rungs: supplyGroupRungs,
    });
  }

  /**
   * SCORE, THEN SEQUENCE. `plan` above is built in the SCORING order (the one
   * the long comment over `NARRATIVE_ORDER`'s neighbours defends); `toPhases`
   * assigns the four states across that flattened rail and only then hands the
   * finished groups to `inNarrativeOrder` for the lane. Reordering the pushes
   * below instead would move the `now` marker — see `NARRATIVE_ORDER`.
   */
  const phases = toPhases(plan, closed, lane);

  return {
    lane,
    entryLabel: ROUTE_LABEL[deal.entryPath] ?? ROUTE_LABEL.widget_form,
    phases,
    band: buildBand(input, closed),
  };
}
